import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useScope } from "@/hooks/use-scope";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Webhook, Plus, RotateCw, Trash2, Send, Eye, RefreshCw, Loader2, Copy, AlertTriangle } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import {
  listEndpoints,
  upsertEndpoint,
  rotateEndpointSecret,
  deleteEndpoint,
  listDeliveries,
  listAttempts,
  replayDelivery,
  sendTestEvent,
} from "@/lib/webhooks.functions";

export const Route = createFileRoute("/_authenticated/app/settings/webhooks")({
  head: () => ({
    meta: [
      { title: "Webhooks — CairnOS" },
      { name: "description", content: "Manage webhook endpoints and inspect delivery history." },
    ],
  }),
  component: WebhooksAdmin,
});

const EVENT_CATALOG = [
  "order.created", "order.updated", "order.cancelled", "order.shipped",
  "shipment.created", "shipment.packed", "shipment.shipped",
  "shipment.delivered", "shipment.voided", "shipment.tracking_updated",
  "shipment.exception",
  "receipt.posted",
  "inventory.adjusted",
  "rma.created", "rma.received",
  "invoice.issued",
  "webhook.test",
];

type StatusKey = "pending" | "in_flight" | "delivered" | "failed" | "dead";

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    delivered: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
    failed: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
    dead: "bg-destructive/10 text-destructive",
    pending: "bg-muted text-muted-foreground",
    in_flight: "bg-blue-500/10 text-blue-600 dark:text-blue-400",
  };
  return <Badge variant="outline" className={map[status] ?? ""}>{status}</Badge>;
}

function WebhooksAdmin() {
  const { tenantId } = useScope();
  const qc = useQueryClient();

  const listEndpointsFn = useServerFn(listEndpoints);
  const upsertFn = useServerFn(upsertEndpoint);
  const rotateFn = useServerFn(rotateEndpointSecret);
  const deleteFn = useServerFn(deleteEndpoint);
  const listDeliveriesFn = useServerFn(listDeliveries);
  const listAttemptsFn = useServerFn(listAttempts);
  const replayFn = useServerFn(replayDelivery);
  const sendTestFn = useServerFn(sendTestEvent);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [revealedSecret, setRevealedSecret] = useState<string | null>(null);

  const [filterEndpoint, setFilterEndpoint] = useState<string>("all");
  const [filterStatus, setFilterStatus] = useState<string>("all");

  const [inspecting, setInspecting] = useState<any>(null);

  const endpointsQ = useQuery({
    queryKey: ["webhook-endpoints", tenantId],
    enabled: !!tenantId,
    queryFn: () => listEndpointsFn({ data: { tenantId: tenantId! } }),
  });

  const deliveriesQ = useQuery({
    queryKey: ["webhook-deliveries", tenantId, filterEndpoint, filterStatus],
    enabled: !!tenantId,
    queryFn: () =>
      listDeliveriesFn({
        data: {
          tenantId: tenantId!,
          endpointId: filterEndpoint === "all" ? null : filterEndpoint,
          status: filterStatus === "all" ? null : (filterStatus as StatusKey),
          limit: 100,
        },
      }),
    refetchInterval: 8_000,
  });

  const attemptsQ = useQuery({
    queryKey: ["webhook-attempts", inspecting?.id],
    enabled: !!inspecting?.id,
    queryFn: () => listAttemptsFn({ data: { outboxId: inspecting.id, limit: 20 } }),
    refetchInterval: inspecting ? 5_000 : false,
  });

  const upsertMut = useMutation({
    mutationFn: (payload: any) => upsertFn({ data: payload }),
    onSuccess: (res) => {
      toast.success(editing?.id ? "Endpoint updated" : "Endpoint created");
      if (res?.signingSecret) setRevealedSecret(res.signingSecret);
      setEditorOpen(false);
      setEditing(null);
      void qc.invalidateQueries({ queryKey: ["webhook-endpoints", tenantId] });
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const rotateMut = useMutation({
    mutationFn: (id: string) => rotateFn({ data: { id } }),
    onSuccess: (res) => {
      setRevealedSecret(res.signingSecret);
      void qc.invalidateQueries({ queryKey: ["webhook-endpoints", tenantId] });
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteFn({ data: { id } }),
    onSuccess: () => {
      toast.success("Endpoint deleted");
      void qc.invalidateQueries({ queryKey: ["webhook-endpoints", tenantId] });
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const replayMut = useMutation({
    mutationFn: (outboxId: string) => replayFn({ data: { outboxId } }),
    onSuccess: () => {
      toast.success("Queued for redelivery");
      void qc.invalidateQueries({ queryKey: ["webhook-deliveries", tenantId] });
      void qc.invalidateQueries({ queryKey: ["webhook-attempts"] });
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const testMut = useMutation({
    mutationFn: (id: string) => sendTestFn({ data: { id, tenantId: tenantId! } }),
    onSuccess: () => {
      toast.success("Test event queued — watch the deliveries table");
      void qc.invalidateQueries({ queryKey: ["webhook-deliveries", tenantId] });
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const endpoints = endpointsQ.data?.rows ?? [];
  const deliveries = deliveriesQ.data?.rows ?? [];
  const endpointById = Object.fromEntries(endpoints.map((e: any) => [e.id, e]));

  return (
    <div className="p-6 space-y-6 max-w-7xl">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <Webhook className="h-6 w-6" /> Webhooks
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Subscribe external systems to operational events. All deliveries are signed, retried,
            and inspectable.
          </p>
        </div>
        <Button onClick={() => { setEditing(null); setEditorOpen(true); }}>
          <Plus className="h-4 w-4 mr-2" /> New endpoint
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Endpoints</CardTitle>
          <CardDescription>Tenant-scoped destinations for outbound events.</CardDescription>
        </CardHeader>
        <CardContent>
          {endpointsQ.isLoading ? (
            <div className="text-sm text-muted-foreground flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : endpoints.length === 0 ? (
            <div className="text-sm text-muted-foreground py-6 text-center">
              No endpoints yet. Create one to start receiving events.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>URL</TableHead>
                  <TableHead>Events</TableHead>
                  <TableHead>Health</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {endpoints.map((ep: any) => (
                  <TableRow key={ep.id}>
                    <TableCell>
                      <div className="font-medium">{ep.name}</div>
                      {ep.description && (
                        <div className="text-xs text-muted-foreground">{ep.description}</div>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-xs max-w-[280px] truncate">{ep.url}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1 max-w-[260px]">
                        {ep.events.slice(0, 3).map((e: string) => (
                          <Badge key={e} variant="secondary" className="text-[10px]">{e}</Badge>
                        ))}
                        {ep.events.length > 3 && (
                          <Badge variant="outline" className="text-[10px]">+{ep.events.length - 3}</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {!ep.is_enabled ? (
                        <Badge variant="outline" className="bg-muted">disabled</Badge>
                      ) : ep.disabled_reason ? (
                        <Badge variant="outline" className="bg-destructive/10 text-destructive">
                          <AlertTriangle className="h-3 w-3 mr-1" /> {ep.disabled_reason}
                        </Badge>
                      ) : ep.consecutive_failures > 0 ? (
                        <Badge variant="outline" className="bg-amber-500/10 text-amber-600 dark:text-amber-400">
                          {ep.consecutive_failures} failing
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
                          healthy
                        </Badge>
                      )}
                      {ep.last_success_at && (
                        <div className="text-[10px] text-muted-foreground mt-1">
                          last ok {formatDistanceToNow(new Date(ep.last_success_at), { addSuffix: true })}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => testMut.mutate(ep.id)} title="Send test">
                          <Send className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => rotateMut.mutate(ep.id)} title="Rotate secret">
                          <RotateCw className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => { setEditing(ep); setEditorOpen(true); }} title="Edit">
                          <Eye className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => { if (confirm(`Delete endpoint "${ep.name}"?`)) deleteMut.mutate(ep.id); }}
                          title="Delete"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div>
            <CardTitle>Delivery Log</CardTitle>
            <CardDescription>Last 100 outbound webhook events. Click a row to inspect attempts.</CardDescription>
          </div>
          <div className="flex gap-2">
            <Select value={filterEndpoint} onValueChange={setFilterEndpoint}>
              <SelectTrigger className="w-[200px]"><SelectValue placeholder="All endpoints" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All endpoints</SelectItem>
                {endpoints.map((e: any) => (
                  <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[140px]"><SelectValue placeholder="Any status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any status</SelectItem>
                <SelectItem value="pending">pending</SelectItem>
                <SelectItem value="in_flight">in_flight</SelectItem>
                <SelectItem value="delivered">delivered</SelectItem>
                <SelectItem value="failed">failed</SelectItem>
                <SelectItem value="dead">dead</SelectItem>
              </SelectContent>
            </Select>
            <Button size="icon" variant="ghost" onClick={() => deliveriesQ.refetch()}>
              <RefreshCw className={`h-4 w-4 ${deliveriesQ.isFetching ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {deliveriesQ.isLoading ? (
            <div className="text-sm text-muted-foreground flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : deliveries.length === 0 ? (
            <div className="text-sm text-muted-foreground py-6 text-center">No deliveries yet.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Event</TableHead>
                  <TableHead>Endpoint</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Attempts</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Last error</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {deliveries.map((d: any) => (
                  <TableRow
                    key={d.id}
                    className="cursor-pointer hover:bg-muted/40"
                    onClick={() => setInspecting(d)}
                  >
                    <TableCell className="font-mono text-xs">{d.event}</TableCell>
                    <TableCell className="text-xs">{endpointById[d.endpoint_id]?.name ?? "—"}</TableCell>
                    <TableCell><StatusBadge status={d.status} /></TableCell>
                    <TableCell className="text-xs">{d.attempts}/{d.max_attempts}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {formatDistanceToNow(new Date(d.created_at), { addSuffix: true })}
                    </TableCell>
                    <TableCell className="text-xs text-destructive max-w-[260px] truncate">
                      {d.last_error ?? ""}
                    </TableCell>
                    <TableCell className="text-right">
                      {(d.status === "failed" || d.status === "dead" || d.status === "delivered") && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => { e.stopPropagation(); replayMut.mutate(d.id); }}
                        >
                          <RotateCw className="h-3.5 w-3.5 mr-1" /> Replay
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <EndpointEditor
        open={editorOpen}
        editing={editing}
        tenantId={tenantId}
        onClose={() => { setEditorOpen(false); setEditing(null); }}
        onSubmit={(payload) => upsertMut.mutate(payload)}
        submitting={upsertMut.isPending}
      />

      <Dialog open={!!revealedSecret} onOpenChange={(o) => !o && setRevealedSecret(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Signing secret</DialogTitle>
            <DialogDescription>
              Copy this now — it will not be shown again. Use it to verify the
              <code className="mx-1">X-Cairnos-Signature</code> header on incoming requests.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2 bg-muted p-3 rounded font-mono text-xs break-all">
            {revealedSecret}
            <Button
              size="icon"
              variant="ghost"
              onClick={() => { navigator.clipboard.writeText(revealedSecret ?? ""); toast.success("Copied"); }}
            >
              <Copy className="h-4 w-4" />
            </Button>
          </div>
          <DialogFooter>
            <Button onClick={() => setRevealedSecret(null)}>I've saved it</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Sheet open={!!inspecting} onOpenChange={(o) => !o && setInspecting(null)}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="font-mono text-sm">{inspecting?.event}</SheetTitle>
            <SheetDescription>
              {inspecting?.id} · {endpointById[inspecting?.endpoint_id]?.name}
            </SheetDescription>
          </SheetHeader>
          {inspecting && (
            <div className="mt-4 space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-muted-foreground">Status:</span> <StatusBadge status={inspecting.status} /></div>
                <div><span className="text-muted-foreground">Attempts:</span> {inspecting.attempts}/{inspecting.max_attempts}</div>
                <div className="col-span-2 text-xs text-muted-foreground">
                  Created {new Date(inspecting.created_at).toLocaleString()}
                </div>
              </div>
              <Button
                size="sm"
                onClick={() => replayMut.mutate(inspecting.id)}
                disabled={replayMut.isPending}
              >
                <RotateCw className="h-3.5 w-3.5 mr-1" /> Redeliver now
              </Button>
              <div>
                <div className="text-xs font-medium uppercase text-muted-foreground mb-2">Attempts</div>
                {attemptsQ.isLoading ? (
                  <div className="text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin inline" /> Loading…</div>
                ) : (attemptsQ.data?.rows ?? []).length === 0 ? (
                  <div className="text-sm text-muted-foreground">No attempts recorded yet.</div>
                ) : (
                  <div className="space-y-2">
                    {(attemptsQ.data?.rows ?? []).map((a: any) => (
                      <div key={a.id} className="border rounded p-3 text-xs space-y-1">
                        <div className="flex justify-between items-center">
                          <div className="font-medium">
                            Attempt #{a.attempt_no}{" "}
                            {a.succeeded ? (
                              <Badge variant="outline" className="bg-emerald-500/10 text-emerald-600">ok</Badge>
                            ) : (
                              <Badge variant="outline" className="bg-destructive/10 text-destructive">failed</Badge>
                            )}
                          </div>
                          <div className="text-muted-foreground">{new Date(a.at).toLocaleString()}</div>
                        </div>
                        <div className="text-muted-foreground">
                          HTTP {a.http_status ?? "—"} · {a.duration_ms ?? "—"}ms
                        </div>
                        {a.error && <div className="text-destructive">{a.error}</div>}
                        {a.response_snippet && (
                          <pre className="bg-muted p-2 rounded overflow-x-auto whitespace-pre-wrap break-all">{a.response_snippet}</pre>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function EndpointEditor({
  open, editing, tenantId, onClose, onSubmit, submitting,
}: {
  open: boolean;
  editing: any;
  tenantId: string | null;
  onClose: () => void;
  onSubmit: (payload: any) => void;
  submitting: boolean;
}) {
  const [name, setName] = useState(editing?.name ?? "");
  const [description, setDescription] = useState(editing?.description ?? "");
  const [url, setUrl] = useState(editing?.url ?? "");
  const [events, setEvents] = useState<string[]>(editing?.events ?? []);
  const [isEnabled, setIsEnabled] = useState<boolean>(editing?.is_enabled ?? true);

  // Reset when target changes.
  useStateSync(editing, () => {
    setName(editing?.name ?? "");
    setDescription(editing?.description ?? "");
    setUrl(editing?.url ?? "");
    setEvents(editing?.events ?? []);
    setIsEnabled(editing?.is_enabled ?? true);
  });

  const toggleEvent = (e: string) =>
    setEvents((prev) => (prev.includes(e) ? prev.filter((x) => x !== e) : [...prev, e]));

  const submit = () => {
    if (!tenantId) return;
    if (!name.trim() || !url.trim() || events.length === 0) {
      toast.error("Name, URL, and at least one event are required");
      return;
    }
    onSubmit({
      id: editing?.id ?? null,
      tenantId,
      name: name.trim(),
      description: description.trim() || null,
      url: url.trim(),
      events,
      headers: {},
      isEnabled,
    });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{editing?.id ? "Edit endpoint" : "New endpoint"}</DialogTitle>
          <DialogDescription>
            Configure where signed event payloads should be POSTed.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label htmlFor="wh-name">Name</Label>
            <Input id="wh-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Shopify sync" />
          </div>
          <div>
            <Label htmlFor="wh-desc">Description</Label>
            <Textarea id="wh-desc" value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
          </div>
          <div>
            <Label htmlFor="wh-url">URL</Label>
            <Input id="wh-url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://example.com/hooks/cairnos" />
          </div>
          <div>
            <Label>Events</Label>
            <div className="flex flex-wrap gap-2 mt-1">
              {EVENT_CATALOG.map((e) => (
                <button
                  key={e}
                  type="button"
                  onClick={() => toggleEvent(e)}
                  className={`text-xs px-2 py-1 rounded border transition ${
                    events.includes(e)
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-background hover:bg-muted"
                  }`}
                >
                  {e}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center justify-between border-t pt-3">
            <div>
              <Label htmlFor="wh-enabled">Enabled</Label>
              <p className="text-xs text-muted-foreground">Disabled endpoints don't receive events.</p>
            </div>
            <Switch id="wh-enabled" checked={isEnabled} onCheckedChange={setIsEnabled} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={submitting}>
            {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {editing?.id ? "Save" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Tiny helper: re-run a side effect when a dep changes (without pulling in useEffect explicitly here).
import { useEffect, useRef } from "react";
function useStateSync(dep: any, fn: () => void) {
  const prev = useRef<any>(undefined);
  useEffect(() => {
    if (prev.current !== dep) {
      prev.current = dep;
      fn();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dep]);
}